/**
 * Wealthica2 navigation provider
 *
 * Responsibilities:
 * - Manage OAuth navigation state (state param, return URL, provider)
 * - Detect return from redirect and prevent reload loops
 * - Provide helpers to save/restore state and redirect to prior location
 *
 * Notes:
 * - Uses `WealthicaStorage` for session-only navigation data
 * - Separate from connection state; no data fetching or persistence here
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { logger } from '@/services/LoggingService';
import { 
  Wealthica2NavigationState, 
  Wealthica2NavigationContextType, 
  ReturnOptions,
  defaultNavigationState,
  NAVIGATION_STORAGE_KEYS
} from './types';
import { WealthicaStorage } from '../../utils/storage';
import { clearAllNavigationData, detectReloadLoop } from './safety';
import { returnToPreviousLocation as returnToPreviousLocationImpl, redirectToInvestments as redirectToInvestmentsImpl } from './redirects';
import { saveToStorage, getFromStorage } from './storage';

// (Removed unused createQueryUrl / extractUrlHashParams)

/**
 * Context for Wealthica navigation management
 * 
 * This context handles all navigation-specific concerns for Wealthica:
 * - OAuth state parameter management
 * - Return URL tracking for redirects
 * - Navigation between different parts of the connection flow
 */
const NavigationContext = createContext<Wealthica2NavigationContextType>({
  navigationState: defaultNavigationState,
  setNavigationState: () => {},
  saveCurrentLocation: () => {},
  returnToPreviousLocation: () => {},
  redirectToInvestments: () => {},
  isReturningFromConnection: false,
  saveState: () => {},
  getState: () => null,
  saveProvider: () => {},
  getProvider: () => null,
  detectReloadLoop: () => false,
  clearAllNavigationData: () => {},
  saveToStorage: () => false,
  getFromStorage: () => null
});

/**
 * Hook for accessing Wealthica navigation context
 * 
 * @returns Navigation context functions and state
 */
export const useWealthica2Navigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    logger.error(
      'NavigationProvider.useWealthica2Navigation', 
      'Hook must be used within Wealthica2NavigationProvider'
    );
    throw new Error('useWealthica2Navigation must be used within Wealthica2NavigationProvider');
  }
  return context;
};

/**
 * Provider component for Wealthica navigation
 * 
 * This component handles all navigation and redirect-related concerns:
 * - Storing state parameters across redirects
 * - Managing return URLs
 * - Controlling navigation flow
 * 
 * This is separate from the Wealthica2Provider which handles connection state
 */
export const Wealthica2NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [navigationState, setNavigationStateInternal] = useState<Wealthica2NavigationState>(defaultNavigationState);
  const [isReturningFromConnection, setIsReturningFromConnection] = useState(false);

  useEffect(() => {
    logger.info('Wealthica2NavigationProvider', 'Initializing navigation provider');
    
    // Simple scan for legacy data
    

    // Hydrate state from WealthicaStorage instead of direct localStorage access
    const storedProvider = WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.PROVIDER);
    const storedState = WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.STATE);
    const storedReturnUrl = WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.RETURN_PATH);

    setNavigationStateInternal(prev => ({
      ...prev,
      provider: storedProvider || prev.provider,
      returnUrl: storedReturnUrl || null
    }));

    // Check if we're returning from OAuth redirect
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('state') && storedState && urlParams.get('state') === storedState) {
      logger.info('Wealthica2NavigationProvider', 'Detected return from OAuth redirect with matching state');
      setIsReturningFromConnection(true);
    }
  }, []);

  /**
   * Update navigation state
   * 
   * @param partialState Partial state to update
   */
  const setNavigationState = (partialState: Partial<Wealthica2NavigationState>) => {
    setNavigationStateInternal(prev => ({
      ...prev,
      ...partialState,
      timestamp: Date.now()
    }));

    // Single-source storage strategy: session-only for navigation state
    if (partialState.provider) {
      WealthicaStorage.setItem(NAVIGATION_STORAGE_KEYS.PROVIDER, partialState.provider, false); // session-only
    }
    
    if (partialState.returnUrl !== undefined) {
      if (partialState.returnUrl) {
        WealthicaStorage.setItem(NAVIGATION_STORAGE_KEYS.RETURN_PATH, partialState.returnUrl, false); // session-only
      } else {
        WealthicaStorage.removeItem(NAVIGATION_STORAGE_KEYS.RETURN_PATH);
      }
    }
  };

  /**
   * Save current provider to storage
   * 
   * @param provider Provider identifier
   */
  const saveProvider = (provider: string) => {
    WealthicaStorage.setItem(NAVIGATION_STORAGE_KEYS.PROVIDER, provider, false); // session-only for navigation
    setNavigationState({ provider });
  };

  /**
   * Get stored provider
   * 
   * @returns Provider identifier or null
   */
  const getProvider = () => WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.PROVIDER) || navigationState.provider || null;

  /**
   * Save OAuth state parameter with enhanced debugging
   * 
   * @param state OAuth state parameter
   */
  const saveState = (state: string) => {
    logger.info('Wealthica2NavigationProvider', '[STATE-LIFECYCLE] Saving OAuth state', {
      state: state.substring(0, 8) + '...',
      stateLength: state.length,
      key: NAVIGATION_STORAGE_KEYS.STATE,
      timestamp: new Date().toISOString(),
      location: {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash
      }
    });
    
    WealthicaStorage.setItem(NAVIGATION_STORAGE_KEYS.STATE, state, false); // session-only for security
    
    // Verify the state was saved correctly
    const verification = WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.STATE);
    if (verification !== state) {
      logger.error('Wealthica2NavigationProvider', '[STATE-LIFECYCLE] State save verification failed', {
        expected: state.substring(0, 8) + '...',
        actual: verification ? verification.substring(0, 8) + '...' : null,
        key: NAVIGATION_STORAGE_KEYS.STATE
      });
    } else {
      logger.info('Wealthica2NavigationProvider', '[STATE-LIFECYCLE] State save verified successfully');
    }
  };

  /**
   * Get OAuth state parameter with enhanced debugging
   * 
   * @returns OAuth state parameter or null
   */
  const getState = () => {
    const state = WealthicaStorage.getItem(NAVIGATION_STORAGE_KEYS.STATE);
    
    logger.info('Wealthica2NavigationProvider', '[STATE-LIFECYCLE] Retrieving OAuth state', {
      hasState: !!state,
      statePreview: state ? state.substring(0, 8) + '...' : null,
      stateLength: state?.length || 0,
      key: NAVIGATION_STORAGE_KEYS.STATE,
      timestamp: new Date().toISOString(),
      location: {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash
      }
    });
    
    return state;
  };

  /**
   * Save current location for return after redirect
   * 
   * @param provider Optional provider to save
   * @param source Optional source identifier
   */
  const saveCurrentLocation = (provider?: string, source?: string) => {
    const currentUrl = window.location.href;
    // Avoid storing connect or investments pages as return URLs to avoid loops
    if (
      window.location.pathname.includes('/investments/connect') ||
      window.location.pathname.endsWith('/investments')
    ) {
      logger.info('Wealthica2NavigationProvider', '[LOOP PROTECTION] Not saving return URL from connect/investments page', { 
        currentUrl 
      });
      return;
    }
    WealthicaStorage.setItem(NAVIGATION_STORAGE_KEYS.RETURN_PATH, currentUrl, false); // session-only for navigation
    setNavigationState({
      returnUrl: currentUrl,
      returnSource: source || 'manual',
      preserveState: true
    });

    if (provider) {
      saveProvider(provider);
    }
  };

  // Lightweight wrappers to bind state into extracted helpers
  const returnToPreviousLocation = (options?: ReturnOptions) => {
    return returnToPreviousLocationImpl(navigationState, options);
  };

  const redirectToInvestments = (message?: string) => {
    return redirectToInvestmentsImpl(message);
  };

  return (
    <NavigationContext.Provider
      value={{
        navigationState,
        setNavigationState,
        saveCurrentLocation,
        returnToPreviousLocation,
        redirectToInvestments,
        isReturningFromConnection,
        saveState,
        getState,
        saveProvider,
        getProvider,
        detectReloadLoop,
        clearAllNavigationData,
        saveToStorage,
        getFromStorage
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};
